// Copyright (c) Docugami, Inc. All rights reserved.

import { MutableRefObject } from "react";
import { IResources, ResourceDependencies } from "sdv/Resources";
import { ISystem, Queries } from "../engine/Engine";
import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { ViewComponent, defaultViewComponent } from "sdv/components/ViewComponent";
import { WorldSpaceConfiguration, scaleValue } from "sdv/Space";

const updateCanvasDeps: ResourceDependencies = ["dpr", "pages", "viewWidth"]

/**
 * Updates our view properties such as position and size based on where the user
 * is in the document and the current canvas properties.
 */
export class UpdateViewSystem implements ISystem {
    private readonly _scrollerRef: MutableRefObject<HTMLDivElement | undefined>;
    private readonly _sdvViewRef: MutableRefObject<HTMLDivElement | undefined>;
    private readonly _canvases: MutableRefObject<HTMLCanvasElement | undefined>[];
    private readonly _spaceConfiguration: WorldSpaceConfiguration;

    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;

    queries: Queries = {
        view: { types: [ComponentId.View] },
    }

    constructor(
        canvases: MutableRefObject<HTMLCanvasElement | undefined>[],
        scrollerRef: MutableRefObject<HTMLDivElement | undefined>,
        sdvViewRef: MutableRefObject<HTMLDivElement | undefined>,
        spaceConfiguration: WorldSpaceConfiguration) {
        this._canvases = canvases;
        this._scrollerRef = scrollerRef;
        this._sdvViewRef = sdvViewRef;
        this._spaceConfiguration = spaceConfiguration
    }

    update() {
        const canvases = this._canvases.filter(canvas => !!canvas.current);
        if (!this.resources || !canvases.length || !this._scrollerRef.current || !this._sdvViewRef.current) {
            return;
        }

        let view = this.queries.view.query!.single();
        if (!view) {
            view = this.entities
                .create()
                .addComponent<ViewComponent>(defaultViewComponent());
        }

        const viewComponent = view.getComponent<ViewComponent>(ComponentId.View)!;
        viewComponent.viewport.width = this._scrollerRef.current?.offsetWidth ?? 0;
        viewComponent.viewport.height = this._scrollerRef.current?.offsetHeight ?? 0;

        viewComponent.view.width = this._sdvViewRef.current?.offsetWidth ?? 0;

        viewComponent.world.width = this._spaceConfiguration.width;
        viewComponent.worldToViewScale = viewComponent.view.width / viewComponent.world.width;
        viewComponent.viewToWorldScale = 1 / viewComponent.worldToViewScale;

        viewComponent.viewport.worldPosition.x = scaleValue(this._scrollerRef.current?.scrollLeft ?? 0, viewComponent.viewToWorldScale);
        viewComponent.viewport.worldPosition.y = scaleValue(this._scrollerRef.current?.scrollTop ?? 0, viewComponent.viewToWorldScale);
        viewComponent.viewport.worldWidth = scaleValue(viewComponent.viewport.width, viewComponent.viewToWorldScale);
        viewComponent.viewport.worldHeight = scaleValue(viewComponent.viewport.height, viewComponent.viewToWorldScale);

        const pages = this.resources.get("pages")!.value;
        if (this.resources.check(updateCanvasDeps) && !!pages?.length) {
            const height = pages[pages.length - 1].position.y + pages[pages.length - 1].height;
            viewComponent.world.height = height;

            viewComponent.view.height = scaleValue(height, viewComponent.worldToViewScale);
            this._sdvViewRef.current.style.height = `${viewComponent.view.height}px`;

            // Mutiply pixel density by at least 2 to give us some room for zoom
            for (const canvas of canvases) {
                canvas.current!.width = scaleValue(viewComponent.world.width, Math.max(window.devicePixelRatio, 2));
                canvas.current!.height = scaleValue(viewComponent.viewport.height, Math.max(window.devicePixelRatio, 2) * viewComponent.viewToWorldScale);
                canvas.current!.style.transformOrigin = "0 0"
                canvas.current!.style.transform = `scale(${(viewComponent.world.width / canvas.current!.width) * viewComponent.worldToViewScale}`
            }
        }

        this.resources.set("viewPosition", viewComponent.viewport.worldPosition);
        this.resources.set("viewWidth", viewComponent.view.width);
        this.resources.set("viewHeight", viewComponent.view.height);
        this.resources.set("worldSpace", viewComponent.world);
        this.resources.set("viewport", viewComponent.viewport);
        this.resources.set("dpr", window.devicePixelRatio);
    }
}

